'use client';
/* eslint-disable @next/next/no-img-element */
import { ElementProps } from '@/components/sections/builder/types';
import { ProductCarouselElementDataType } from '@/components/sections/builder/elements/body/product-carousel/type';
import { useProductCollections } from '@/hooks/api/storefront/product-collection';
import CardProductBuilder from '@/components/sections/builder/elements/body/card-product';
import { Button } from '@/components/ui/button';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import Link from 'next/link';
import { ProductListElementDataType } from '@/components/sections/builder/elements/body/product-grid/type';
import { ProductGridElementDataType } from '@/components/sections/builder/elements/body/collection-product-grid/type';
import { cn, isEmpty } from '@/lib/utils';
import React from 'react';
import Autoplay from 'embla-carousel-autoplay';
import { useSearchParams } from 'next/navigation';
import Cookies from 'js-cookie';
import SkeletonProductCarousel from '@/components/sections/builder/elements/body/product-carousel/skeleton';

export default function ProductCarouselContent({ element, preview, host, theme }: ElementProps) {
  const data = element.data as ProductCarouselElementDataType;
  const autoplay = data.autoplay ? true : false;
  const query = useSearchParams();
  const themeId = query.get('theme_id');
  const customerToken = Cookies.get('accessToken');

  const params = {
    slug: data.source?.slug as string,
    hostname: host as string,
    page: 1,
    pageSize: data.limit as number,
    limit: data.limit,
  };

  const { data: productData, isLoading } = useProductCollections(params, customerToken as string);

  const products = productData?.data || [];
  const collectionAdminDesc = productData?.description;

  const delay = data.autoplay ? (data?.changeSlidesEvery || 5) * 1000 : 0;
  const plugin = React.useRef(
    Autoplay({
      delay: delay,
      playOnInit: autoplay,
    })
  );

  return (
    <>
      <section
        className={cn(
          'color-scheme-carousel',
          data.background?.type === 'color' && 'background-color',
          data.background?.type === 'image' && 'background-image'
        )}
      >
        <div className='theme_layout_page_width mx-auto duration-100'>
          <div className='product-carousel-padding relative'>
            {data.overlay && <div className={cn('overlay absolute inset-0')} />}
            <div className='relative p-3'>
              <div
                className={cn(
                  'flex flex-col',
                  data.contentAlign === 'left' && '[&>*]:text-left',
                  data.contentAlign === 'center' && '[&>*]:text-center',
                  data.contentAlign === 'right' && '[&>*]:text-right'
                )}
              >
                <div className={cn('mb-4', data.positionViewAll === 'top' && 'w-full')}>
                  <div
                    className={cn(
                      'flex items-center gap-2 md:gap-3 xl:gap-4',
                      data.contentAlign === 'left' && 'justify-between',
                      data.contentAlign === 'center' && 'justify-center',
                      data.contentAlign === 'right' && 'flex-row-reverse justify-between'
                    )}
                  >
                    <h2
                      className={cn(
                        'theme_font_heading text-base font-bold tracking-tight',
                        data.headingSize === 'large' &&
                          'text-xl sm:text-2xl md:text-3xl lg:text-4xl',
                        data.headingSize === 'medium' &&
                          'text-lg sm:text-xl md:text-2xl lg:text-3xl',
                        data.headingSize === 'small' &&
                          'text-base sm:text-lg md:text-xl lg:text-2xl'
                      )}
                      data-animation='true'
                      dangerouslySetInnerHTML={{ __html: data.headingLabel as string }}
                    />

                    {data.showViewAll && (
                      <Link
                        href={`/collections/${data.source?.slug as string}${
                          preview ? `?isPreview=true&theme_id=${themeId}` : ''
                        }`}
                        className={cn(data.positionViewAll === 'bottom' && 'hidden')}
                      >
                        <Button
                          variant={data.viewAllStyle as 'default'}
                          className={cn(
                            'theme_font_heading text-sm font-bold tracking-tight sm:text-base',
                            data.viewAllStyle === 'link' && 'p-0'
                          )}
                          style={
                            !isEmpty(data.colorScheme) && data.viewAllStyle === 'default'
                              ? {
                                  backgroundColor: data.colorScheme?.btnSolidBackgroud,
                                  color: data?.colorScheme?.btnSolidlabel,
                                }
                              : data.viewAllStyle === 'link'
                              ? {
                                  color: data.colorScheme?.btnSolidlabel,
                                }
                              : {}
                          }
                        >
                          {data.labelViewAll}
                        </Button>
                      </Link>
                    )}
                  </div>

                  {data.showAdminCollectionDesc ||
                    (data.textLabel && (
                      <div
                        className={cn(
                          'minimal-tiptap-editor tiptap ProseMirror',
                          data.textStyle === 'body' && 'text-base md:text-lg lg:text-xl',
                          data.textStyle === 'subtitle' && 'text-sm md:text-base lg:text-lg',
                          data.textStyle === 'uppercase' &&
                            'text-xs uppercase md:text-sm lg:text-base'
                        )}
                        data-animation='true'
                        dangerouslySetInnerHTML={{
                          __html: data.showAdminCollectionDesc
                            ? (collectionAdminDesc as string)
                            : (data.textLabel as string),
                        }}
                      />
                    ))}
                </div>

                <div
                  className={cn(
                    `product-carousel-padding  grid-cols-${Number(
                      data.totalColMobile
                    )} sm:grid-cols-${data.totalCol} md:grid-cols-${data.totalCol} lg:grid-cols-${
                      data.totalCol
                    } theme_layout_space_grid_horizontal theme_layout_space_grid_vertical hidden place-content-center`,
                    data.fullWidth && 'full_width_product_carousel',
                    !data.enableCarousel && 'md:grid',
                    data.size === 'small' &&
                      '[&>article>div>div>div]:text-xs [&>article>div>div>h2]:text-base [&>article>div>div>p]:text-xs [&>article>div>div>span]:text-xs',
                    data.size === 'medium' &&
                      '[&>article>div>div>div]:text-sm [&>article>div>div>h2]:text-lg [&>article>div>div>p]:text-sm [&>article>div>div>span]:text-sm',
                    data.size === 'large' &&
                      '[&>article>div>div>div]:text-sm [&>article>div>div>h2]:text-lg [&>article>div>div>p]:text-sm [&>article>div>div>span]:text-sm'
                  )}
                >
                  {isLoading
                    ? Array.from({ length: 6 }).map((_, index) => (
                        <SkeletonProductCarousel key={index} />
                      ))
                    : products.map((product) => (
                        <CardProductBuilder
                          data={data as ProductListElementDataType | ProductGridElementDataType}
                          product={product}
                          key={product.id}
                          element={element}
                          preview={preview}
                          theme={theme}
                        />
                      ))}
                </div>
                <div
                  className={cn(
                    'product-carousel-padding place-content-center',
                    data.fullWidth && 'full_width_product_carousel',
                    !data.enableCarousel && 'md:hidden'
                  )}
                >
                  <Carousel
                    className='max-w-full'
                    plugins={autoplay ? [plugin.current] : []}
                    opts={{ loop: true }}
                  >
                    <CarouselContent className={cn('items-stretch', isLoading && 'gap-2 lg:gap-4')}>
                      {isLoading
                        ? Array.from({ length: 4 }).map((_, index) => (
                            <SkeletonProductCarousel key={index} />
                          ))
                        : products.map((product) => (
                            <CarouselItem
                              style={{
                                flexBasis: `var(--flex-basis-product-carousel)`,
                              }}
                              className='carousel-item-responsive flex items-stretch'
                              key={product.id}
                            >
                              <div className='flex h-full w-full flex-col'>
                                <CardProductBuilder
                                  data={
                                    data as ProductListElementDataType | ProductGridElementDataType
                                  }
                                  product={product}
                                  element={element}
                                  preview={preview}
                                  theme={theme}
                                />
                              </div>
                            </CarouselItem>
                          ))}
                    </CarouselContent>
                    <CarouselPrevious className='left-4 md:h-12 md:w-12 md:[&>svg]:h-6 md:[&>svg]:w-6' />
                    <CarouselNext className='right-4 md:h-12 md:w-12 md:[&>svg]:h-6 md:[&>svg]:w-6' />
                  </Carousel>
                </div>

                {data.showViewAll && (
                  <Link
                    href={`/collections/${data.source?.slug as string}${
                      preview ? `?isPreview=true&theme_id=${themeId}` : ''
                    }`}
                    className={cn('p-0', data.positionViewAll === 'top' && 'hidden')}
                  >
                    <Button
                      variant={data.viewAllStyle as 'default'}
                      className={cn(
                        'theme_font_heading mx-auto my-5 flex  items-center px-4 text-sm font-bold tracking-tight sm:text-base',
                        data.viewAllStyle === 'link' && 'p-0'
                      )}
                      style={
                        !isEmpty(data.colorScheme) && data.viewAllStyle === 'default'
                          ? {
                              backgroundColor: data.colorScheme?.btnSolidBackgroud,
                              color: data?.colorScheme?.btnSolidlabel,
                            }
                          : data.viewAllStyle === 'link'
                          ? {
                              color: data.colorScheme?.btnSolidlabel,
                            }
                          : {}
                      }
                    >
                      {data.labelViewAll}
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      <style jsx>
        {`
          @media (min-width: 768px) {
            .product-carousel-padding {
              padding-top: ${data.padding?.top || 0}px;
              padding-bottom: ${data.padding?.bottom || 0}px;
              padding-left: ${data.padding?.left || 0}px;
              padding-right: ${data.padding?.right || 0}px;
            }
            .full_width_product_carousel {
              width: calc(100vw - 20px);
              margin-left: calc(-50vw + 50% + 10px);
            }
          }
          .card-rounded {
            border-radius: ${data.rounded || 0}px;
          }
          .overlay {
            background-color: ${data.overlay};
          }
          .color-scheme-carousel {
            background-color: ${data?.colorScheme?.background};
            color: ${data?.colorScheme?.text || 'inherit'};
          }
          .background-color {
            background-color: ${data.background?.backgroundColor};
            --tw-bg-opacity: ${data.background?.backgroundOpacity};
          }
          .background-image {
            background-image: url(${data.background?.backgroundImage});
            background-repeat: no-repeat;
            background-size: ${data.background?.backgroundSize};
            background-position: ${data.background?.backgroundPosition};
            background-attachment: ${data.background?.backgroundAttachment};
          }
        `}
      </style>
      <style jsx global>{`
        .carousel-item-responsive {
          --flex-basis-product-carousel: calc(
            100% / ${data.totalColMobile || 1}
          ); /* Mobile default */
        }

        .carousel-item-responsive {
          display: flex;
          align-items: stretch; /* Pastikan semua item meregang */
        }

        .carousel-item-responsive > div {
          display: flex;
          flex-direction: column;
          height: 100%; /* Paksa semua item memiliki tinggi penuh */
        }

        @media (min-width: 768px) {
          .carousel-item-responsive {
            --flex-basis-product-carousel: calc(100% / ${data.totalCol || 4}); /* Desktop */
          }
        }
      `}</style>
    </>
  );
}
